import { Link } from "react-router-dom";

export default function HeroSection() {
  return (
    <section className="relative min-h-[700px] md:min-h-[760px] w-full overflow-hidden flex items-center">
      {/* Background image */}
      <img
        src="https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/459450b1e85b2f2e28083a9905c6a883.png"
        alt="Roll Press Servo Hydraulic Equipment - MC TECH"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* Lighter left-to-right gradient — equipment visible, text still readable */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/75 via-primary-950/50 to-transparent" />
      <div className="absolute inset-0 bp-grid opacity-10" />

      {/* Right-side tech labels */}
      <div className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-end gap-6 md:gap-8">
        {[
          { label: "PRESSURE", width: "w-20" },
          { label: "POSITION", width: "w-28" },
          { label: "GAP CONTROL", width: "w-36" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-3">
            <div className={`h-px ${item.width} bg-accent-400/40`} />
            <span className="text-[11px] font-semibold tracking-[0.2em] text-accent-300/80 uppercase">
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-2xl">
          {/* Top pill tag */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-background-100">
              Roll Press · Servo Hydraulic · Motion Control
            </span>
          </div>

          {/* Title - 크기 조정 */}
          <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-background-50 leading-[1.25] tracking-tight">
            롤프레스
            <br />
            <span className="text-accent-400">정밀 제어</span> 솔루션
          </h1>

          {/* Body with accent left border */}
          <div className="mt-6 md:mt-8 border-l-2 border-accent-400 pl-4 md:pl-5">
            <p className="text-base md:text-lg text-background-200/90 max-w-xl leading-relaxed">
              MC TECH는 롤프레스 장비에 필요한 유압 유니트, 서보밸브, 유압 실린더,
              위치·압력 센서 및 모션 컨트롤러를 기반으로 설계·구성·파라미터 설정·시운전까지
              지원합니다.
            </p>
          </div>

          {/* Control tags */}
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-y-2 text-sm text-background-100 font-medium">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-dashboard-line w-4 h-4 flex items-center justify-center text-accent-300" />
              Pressure Control
            </span>
            <span className="text-background-300/60 mx-2 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-focus-3-line w-4 h-4 flex items-center justify-center text-accent-300" />
              Position Control
            </span>
            <span className="text-background-300/60 mx-2 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-arrow-up-down-line w-4 h-4 flex items-center justify-center text-accent-300" />
              Gap Control
            </span>
            <span className="text-background-300/60 mx-2 hidden sm:inline">|</span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-pulse-line w-4 h-4 flex items-center justify-center text-accent-300" />
              Motion Control
            </span>
          </div>

          {/* Buttons */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to="/roll-press"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center" />
              롤프레스 솔루션 보기
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md border border-background-50/40 hover:bg-background-50/10 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center" />
              견적 문의
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>

          {/* 4 capability cards */}
          <div className="mt-10 md:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: "ri-shield-check-line", label: "MOOG 기반", sub: "제품 공급" },
              { icon: "ri-settings-5-line", label: "유압·전기", sub: "통합 설계" },
              { icon: "ri-equalizer-line", label: "파라미터 설정", sub: "및 시운전" },
              { icon: "ri-customer-service-2-line", label: "현장", sub: "기술지원" },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-background-50/5 backdrop-blur border border-background-50/15 rounded-lg px-4 py-3.5 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full border border-accent-400/40 bg-transparent flex items-center justify-center shrink-0">
                  <i className={`${s.icon} w-4 h-4 flex items-center justify-center text-accent-400`} />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-semibold text-background-100">{s.label}</span>
                  <span className="text-[11px] text-background-200/80">{s.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-1">
          <span className="text-[10px] font-semibold tracking-[0.2em] text-background-300/70 uppercase">
            Scroll
          </span>
          <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-background-300/70 animate-bounce" />
        </div>
      </div>
    </section>
  );
}